import registry from './data/hockeyPositions.json';

export type HockeyPositionLocale = 'nl' | 'en';
export type HockeyPositionNames = Readonly<Record<HockeyPositionLocale, string>>;
interface HockeyPositionRegistry {
  schemaVersion: number;
  status: 'accepted';
  acceptedOn: string;
  positions: ReadonlyArray<{ key: string; names: HockeyPositionNames }>;
  legacyDisplayReferences: Readonly<Record<string, string>>;
  ambiguousLegacyKeys: ReadonlyArray<string>;
  formationSlots: Readonly<Record<string, Readonly<Record<string, string>>>>;
  formationContexts: Readonly<Record<string, { type: 'indoor' | 'outdoor'; lines: Readonly<Record<string, string | null>> }>>;
  observedLabels: Readonly<Record<string, Readonly<Record<HockeyPositionLocale, ReadonlyArray<string>>>>>;
  preferredSlotKeys: Readonly<Record<string, string>>;
  writePolicy: { referenceField: 'position_ref'; allowNewCustomPositions: false; requiredLocales: ReadonlyArray<HockeyPositionLocale>; standardLabelsEditable: false };
}

/** Authoritative names and write bindings. These references never define player eligibility aliases. */
export const hockeyPositionRegistry = registry as HockeyPositionRegistry;
const names = new Map(hockeyPositionRegistry.positions.map(position => [position.key, position.names]));
const defaults = new Map([
  ...hockeyPositionRegistry.positions.map(position => [position.key.toLowerCase(), position.key] as const),
  ...Object.entries(hockeyPositionRegistry.legacyDisplayReferences).map(([slot, reference]) => [slot.toLowerCase(), reference] as const),
]);

export const getHockeyPositionNames = (reference: string): HockeyPositionNames | undefined => names.get(reference);

export const getHockeyFormationReference = (formationKey: string, slotKey: string): string | undefined => {
  if (!Object.prototype.hasOwnProperty.call(hockeyPositionRegistry.formationSlots, formationKey)) return undefined;
  const slots = hockeyPositionRegistry.formationSlots[formationKey];
  return Object.prototype.hasOwnProperty.call(slots, slotKey) ? slots[slotKey] : undefined;
};

/** Context-free legacy preferences retain their historical role: sweeper is Voorstopper, never Laatste. */
export const getDefaultHockeyPositionLabel = (slotKey: string, locale: HockeyPositionLocale): string | undefined => {
  const reference = defaults.get(slotKey.toLowerCase());
  return reference ? getHockeyPositionNames(reference)?.[locale] : undefined;
};

export interface HockeyFormationSlotContext { key: string; lineKey: string | null }
export interface HockeyFormationDisplayContext { formationKey?: string; type?: string }

/** Match the entire reviewed slot/line context. Coordinates and declared line counts are deliberately irrelevant. */
export const matchHockeyFormationContext = (
  slots: ReadonlyArray<HockeyFormationSlotContext>, context: HockeyFormationDisplayContext = {},
): string | undefined => {
  const candidates = context.formationKey === undefined ? Object.keys(hockeyPositionRegistry.formationContexts) : [context.formationKey];
  return candidates.find(key => {
    if (!Object.prototype.hasOwnProperty.call(hockeyPositionRegistry.formationContexts, key)) return false;
    const expected = hockeyPositionRegistry.formationContexts[key];
    return expected && (!context.type || context.type === expected.type)
      && slots.length === Object.keys(expected.lines).length
      && new Set(slots.map(slot => slot.key)).size === slots.length
      && slots.every(slot => Object.prototype.hasOwnProperty.call(expected.lines, slot.key) && expected.lines[slot.key] === slot.lineKey);
  });
};

/** Standard roles use configured names; an unresolved custom role retains its original text. */
export const getReviewedHockeyPositionLabel = (
  slotKey: string, storedLabel: string, locale: HockeyPositionLocale, matchedFormationKey?: string,
): string => {
  const reference = getHockeyPositionDisplayReference(slotKey, {}, matchedFormationKey);
  const accepted = reference && getHockeyPositionNames(reference)?.[locale];
  if (!accepted) return storedLabel;
  return accepted;
};

/** Standard roles use registry names, including old overrides. Unmapped custom positions remain readable. */
export const getHockeyPositionDisplayReference = (slotKey: string, node: Record<string, unknown>, matchedFormationKey?: string): string | undefined => {
  const trusted = (matchedFormationKey && getHockeyFormationReference(matchedFormationKey, slotKey))
    || (!hockeyPositionRegistry.ambiguousLegacyKeys.includes(slotKey)
      ? names.has(slotKey) ? slotKey : Object.prototype.hasOwnProperty.call(hockeyPositionRegistry.legacyDisplayReferences, slotKey)
        ? hockeyPositionRegistry.legacyDisplayReferences[slotKey] : undefined : undefined);
  if (trusted) return trusted;
  const explicit = node[hockeyPositionRegistry.writePolicy.referenceField];
  if (typeof explicit !== 'string' || !names.has(explicit)) return undefined;
  if (slotKey === 'sweeper' && !['markingCentreBack', 'defensiveMidfield'].includes(explicit)) return undefined;
  return explicit;
};

export interface HockeyPositionWriteSource extends HockeyFormationDisplayContext { positions: unknown }
export interface HockeyPositionLeaf {
  slotKey: string; lineKey: string | null; path: string[]; node: Record<string, unknown>;
}
export interface HockeyPositionWriteIssue { path: string[]; code: string; locale?: HockeyPositionLocale; expected?: string }
export class HockeyPositionWriteError extends Error {
  readonly code = 'FORMATION_POSITION_VALIDATION_FAILED';
  constructor(readonly errors: HockeyPositionWriteIssue[]) { super('FORMATION_POSITION_VALIDATION_FAILED'); }
}

/** Only player leaves participate; field and line metadata are retained verbatim. */
export const getHockeyPositionLeaves = (positions: unknown): HockeyPositionLeaf[] => {
  if (!positions || typeof positions !== 'object' || Array.isArray(positions)) return [];
  const entries = Object.entries(positions).filter(([key]) => key !== '__meta');
  const hierarchical = entries.some(([, value]) => value && typeof value === 'object' && 'line_label' in value);
  const leaf = (slotKey: string, node: unknown, lineKey: string | null): HockeyPositionLeaf[] =>
    node && typeof node === 'object' && !Array.isArray(node) && 'x' in node && 'y' in node
      ? [{ slotKey, lineKey, path: lineKey === null ? [slotKey] : [lineKey, slotKey], node: node as Record<string, unknown> }] : [];
  return entries.flatMap(([key, value]) => {
    if (!hierarchical) return leaf(key, value, null);
    if (!value || typeof value !== 'object' || !('line_label' in value)) return [];
    return Object.entries(value).flatMap(([slot, node]) => slot.startsWith('line_') || slot === 'order' ? [] : leaf(slot, node, key));
  });
};

const inferredWriteReferences = (leaves: HockeyPositionLeaf[], context: HockeyFormationDisplayContext) => {
  // A copy's fresh formation key must not erase its reviewed source layout.
  const shape = matchHockeyFormationContext(leaves.map(leaf => ({ key: leaf.slotKey, lineKey: leaf.lineKey })), { type: context.type });
  return new Map(leaves.map(leaf => [leaf.slotKey, (shape && getHockeyFormationReference(shape, leaf.slotKey))
    || (!hockeyPositionRegistry.ambiguousLegacyKeys.includes(leaf.slotKey)
      ? names.has(leaf.slotKey) ? leaf.slotKey
        : Object.prototype.hasOwnProperty.call(hockeyPositionRegistry.legacyDisplayReferences, leaf.slotKey)
          ? hockeyPositionRegistry.legacyDisplayReferences[leaf.slotKey] : undefined : undefined)]));
};

export interface HockeyPositionWriteBinding extends HockeyPositionLeaf {
  reference?: string; originalReference?: string; inferredReference?: string; labelsDiffer: boolean;
}

/** Inspect without changing the form. Baseline bindings survive deliberate moves and formation-key edits. */
export const getHockeyPositionWriteBindings = (
  positions: unknown, context: HockeyFormationDisplayContext = {}, original?: HockeyPositionWriteSource,
): HockeyPositionWriteBinding[] => {
  const leaves = getHockeyPositionLeaves(positions);
  const inferred = inferredWriteReferences(leaves, context);
  const originalLeaves = getHockeyPositionLeaves(original?.positions);
  const originalInferred = inferredWriteReferences(originalLeaves, original ?? {});
  const originalReferences = new Map(originalLeaves.map(leaf => {
    const explicit = leaf.node[hockeyPositionRegistry.writePolicy.referenceField];
    return [leaf.slotKey, originalInferred.get(leaf.slotKey) || (typeof explicit === 'string' && names.has(explicit) ? explicit : undefined)];
  }));
  return leaves.map(leaf => {
    const explicit = leaf.node[hockeyPositionRegistry.writePolicy.referenceField];
    const originalReference = originalReferences.get(leaf.slotKey);
    const reference = originalReference || inferred.get(leaf.slotKey) || (typeof explicit === 'string' && names.has(explicit) ? explicit : undefined);
    const accepted = reference && getHockeyPositionNames(reference);
    const labels = leaf.node.label_translations as Record<string, unknown> | undefined;
    return { ...leaf, reference, originalReference, inferredReference: originalReference || inferred.get(leaf.slotKey),
      labelsDiffer: Boolean(accepted && hockeyPositionRegistry.writePolicy.requiredLocales.some(locale => labels?.[locale] !== accepted[locale])) };
  });
};

export const getHockeyPositionPreferredSlotKey = (reference: string): string | undefined =>
  names.has(reference) ? hockeyPositionRegistry.preferredSlotKeys[reference] : undefined;

/** Canonicalise on an explicit write only. No mutation, slot renaming, eligibility alias or geometry adjustment. */
export const prepareHockeyPositionsForWrite = (
  positions: Record<string, unknown>, context: HockeyFormationDisplayContext = {}, original?: HockeyPositionWriteSource,
): Record<string, unknown> => {
  const bindings = getHockeyPositionWriteBindings(positions, context, original);
  const originalLeaves = getHockeyPositionLeaves(original?.positions);
  const issues: HockeyPositionWriteIssue[] = [];
  const seen = new Set<string>();
  for (const binding of bindings) {
    if (seen.has(binding.slotKey)) issues.push({ path: ['positions', ...binding.path], code: 'POSITION_STRUCTURE_INVALID' });
    seen.add(binding.slotKey);
  }
  const result = structuredClone(positions);
  const referenceField = hockeyPositionRegistry.writePolicy.referenceField;
  for (const binding of bindings) {
    const { slotKey, node, path, reference, inferredReference } = binding;
    const explicit = node[referenceField];
    const issue = (code: string) => issues.push({ path: ['positions', ...path, referenceField], code, ...(reference ? { expected: reference } : {}) });
    if (explicit != null && (typeof explicit !== 'string' || !names.has(explicit))) { issue('POSITION_REFERENCE_UNKNOWN'); continue; }
    if (explicit && inferredReference && explicit !== inferredReference) { issue('POSITION_REFERENCE_CONFLICT'); continue; }
    if (slotKey === 'lastLine' && reference && reference !== 'lastDefender'
      || slotKey === 'sweeper' && reference && !['markingCentreBack', 'defensiveMidfield'].includes(reference)) { issue('POSITION_REFERENCE_CONFLICT'); continue; }
    if (!reference) {
      const previous = originalLeaves.find(leaf => leaf.slotKey === slotKey && JSON.stringify(leaf.path) === JSON.stringify(path));
      if (!previous || JSON.stringify(previous.node.label_translations) !== JSON.stringify(node.label_translations)
        || previous.node[referenceField] !== explicit) issue('CUSTOM_POSITION_NOT_ALLOWED');
      continue;
    }
    const target = path.reduce<Record<string, unknown>>((parent, key) => parent[key] as Record<string, unknown>, result);
    target[referenceField] = reference;
    if (target.custom === true) target.custom = false;
    target.label_translations = { ...(node.label_translations && typeof node.label_translations === 'object' ? node.label_translations : {}), ...getHockeyPositionNames(reference) };
  }
  if (issues.length) throw new HockeyPositionWriteError(issues);
  return result;
};
